import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { CallSettings, UpdateCallSettingsPayload } from "./types";
import { getCallSettings, updateCallSettings } from "./api";

interface CallSettingsState {
  settings: CallSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: CallSettingsState = {
  settings: null,
  loading: false,
  saving: false,
  error: null,
};

// Async thunks
export const fetchCallSettings = createAsyncThunk(
  "callSettings/fetchCallSettings",
  async (_, { rejectWithValue }) => {
    try {
      return await getCallSettings();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch call settings";
      return rejectWithValue(message);
    }
  },
);

export const saveCallSettings = createAsyncThunk(
  "callSettings/saveCallSettings",
  async (payload: UpdateCallSettingsPayload, { rejectWithValue }) => {
    try {
      const response = await updateCallSettings(payload);
      return response.settings;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save call settings";
      return rejectWithValue(message);
    }
  },
);

const callSettingsSlice = createSlice({
  name: "callSettings",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSettings: (state, action: PayloadAction<CallSettings>) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder
      .addCase(fetchCallSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCallSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchCallSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Save settings
    builder
      .addCase(saveCallSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveCallSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(saveCallSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setSettings } = callSettingsSlice.actions;
export default callSettingsSlice.reducer;
